import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';

export interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  createdAt: string;
}

export interface OrderItem {
  id: string;
  productId: string;
  productTitle: string;
  productImage: string;
  price: string;
  priceNum: number;
  type: 'book' | 'app';
  purchasedAt: string;
  status: 'completed';
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  orders: OrderItem[];
  login: (email: string, password: string) => Promise<void>;
  signup: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  addOrder: (item: Omit<OrderItem, 'id' | 'purchasedAt' | 'status'>) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

const STORAGE_KEY = 'jl_store_user';
const ORDERS_KEY = 'jl_store_orders';

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [orders, setOrders] = useState<OrderItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) setUser(JSON.parse(stored));
      const storedOrders = localStorage.getItem(ORDERS_KEY);
      if (storedOrders) setOrders(JSON.parse(storedOrders));
    } catch (_) {
      // ignore corrupt storage
    }
    setIsLoading(false);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    if (!email || !password) throw new Error('Email and password are required.');
    // NOTE: Replace this block with Supabase auth once connected.
    const mockUser: User = {
      id: `user_${Date.now()}`,
      name: email.split('@')[0].replace(/[._]/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      email,
      createdAt: new Date().toISOString(),
    };
    setUser(mockUser);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(mockUser));
  }, []);

  const signup = useCallback(async (name: string, email: string, password: string) => {
    if (!name || !email || !password) throw new Error('All fields are required.');
    // NOTE: Replace this block with Supabase auth once connected.
    const newUser: User = {
      id: `user_${Date.now()}`,
      name,
      email,
      createdAt: new Date().toISOString(),
    };
    setUser(newUser);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newUser));
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const addOrder = useCallback((item: Omit<OrderItem, 'id' | 'purchasedAt' | 'status'>) => {
    const order: OrderItem = {
      ...item,
      id: `order_${Date.now()}`,
      purchasedAt: new Date().toISOString(),
      status: 'completed',
    };
    setOrders(prev => {
      const updated = [order, ...prev];
      localStorage.setItem(ORDERS_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  return (
    <AuthContext.Provider value={{ user, isLoading, orders, login, signup, logout, addOrder }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
